import { dayNumber, interval } from "../engine/rules";

// Compares a person's planned shifts with their clock entries, in the shop's
// timezone. Pure; see docs/plans/workday/staff_planning.md (phase 5).

export type PlannedItem = { shift_id: number; date: string; start_time: string; end_time: string };
export type EntryItem = { id: number; clock_in: string; clock_out: string | null; shift_id: number | null };

export type Flag = "late" | "left_early" | "no_show" | "still_in" | "unplanned";

/** Minutes since 1970 on the wall clock of `timeZone` for an instant. */
export function wallMinutes(iso: string, timeZone: string) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
      .formatToParts(new Date(iso))
      .map((p) => [p.type, p.value]),
  );
  return dayNumber(`${parts.year}-${parts.month}-${parts.day}`) * 1440 + Number(parts.hour) * 60 + Number(parts.minute);
}

const GRACE = 10;

export type DayRow = {
  planned: PlannedItem | null;
  entry: EntryItem | null;
  flags: Flag[];
  lateBy?: number;
};

/** Pairs shifts with entries (by link, else overlap) and flags each pair. */
export function compareDay(planned: PlannedItem[], entries: EntryItem[], timeZone: string, nowIso: string): DayRow[] {
  const now = wallMinutes(nowIso, timeZone);
  const used = new Set<number>();
  const rows: DayRow[] = [];
  for (const p of planned) {
    const iv = interval({ shift_date: p.date, start_time: p.start_time, end_time: p.end_time });
    let entry = entries.find((e) => !used.has(e.id) && e.shift_id === p.shift_id) ?? null;
    if (!entry) {
      entry =
        entries.find((e) => {
          if (used.has(e.id) || e.shift_id != null) return false;
          const a = wallMinutes(e.clock_in, timeZone);
          const b = e.clock_out ? wallMinutes(e.clock_out, timeZone) : now;
          return a < iv.end && iv.start < b;
        }) ?? null;
    }
    const flags: Flag[] = [];
    let lateBy: number | undefined;
    if (!entry) {
      if (now > iv.end) flags.push("no_show");
    } else {
      used.add(entry.id);
      const a = wallMinutes(entry.clock_in, timeZone);
      if (a > iv.start + GRACE) {
        flags.push("late");
        lateBy = a - iv.start;
      }
      if (entry.clock_out) {
        if (wallMinutes(entry.clock_out, timeZone) < iv.end - GRACE) flags.push("left_early");
      } else if (now > iv.end + 60) {
        flags.push("still_in");
      }
    }
    rows.push({ planned: p, entry, flags, lateBy });
  }
  for (const e of entries) {
    if (used.has(e.id)) continue;
    const flags: Flag[] = ["unplanned"];
    if (!e.clock_out && now > wallMinutes(e.clock_in, timeZone) + 12 * 60) flags.push("still_in");
    rows.push({ planned: null, entry: e, flags });
  }
  return rows;
}

/** The shop's wall-clock date and HH:MM for an instant. */
export function wallTime(iso: string, timeZone: string) {
  const m = wallMinutes(iso, timeZone);
  const day = Math.floor(m / 1440);
  const mins = m - day * 1440;
  return {
    date: new Date(day * 86_400_000).toISOString().slice(0, 10),
    time: `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`,
  };
}

/** The instant (ISO, UTC) of a wall-clock date and time in `timeZone`. */
export function zonedIso(date: string, time: string, timeZone: string) {
  const wall = Date.UTC(+date.slice(0, 4), +date.slice(5, 7) - 1, +date.slice(8, 10), +time.slice(0, 2), +time.slice(3, 5));
  // Correct by the zone's offset; twice, in case the first guess crossed a DST change.
  let utc = wall;
  for (let i = 0; i < 2; i++) {
    const offset = wallMinutes(new Date(utc).toISOString(), timeZone) - Math.floor(utc / 60_000);
    utc = wall - offset * 60_000;
  }
  return new Date(utc).toISOString();
}
